import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import "./index.css";

const CLIENT_AUTH_KEY = "socialhub_client_session";

function getClientToken(): string | null {
  try {
    const s = localStorage.getItem(CLIENT_AUTH_KEY);
    if (!s) return null;
    const session = JSON.parse(s);
    return session?.token ?? null;
  } catch {
    return null;
  }
}

// Tenant switcher: raz admin pode escolher qual cliente está ativo. Esse valor
// vai no header de TODA requisição tRPC. Backend (context.ts) só respeita pra
// raz admin — usuário comum tem o tenant travado no JWT.
function getActiveClientHeader(): string | null {
  try {
    const s = localStorage.getItem(CLIENT_AUTH_KEY);
    if (!s) return null;
    const session = JSON.parse(s);
    if (!session?.isRazAdmin) return null;
    const id = session?.activeClientId;
    if (id == null) return "all"; // raz admin pediu "Todos os clientes"
    return String(id);
  } catch {
    return null;
  }
}

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = "/login";
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      headers() {
        const token = getClientToken();
        const active = getActiveClientHeader();
        const h: Record<string, string> = {};
        if (token) h["x-client-token"] = token;
        if (active) h["x-active-client"] = active;
        return h;
      },
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
